'use client'
import { useEffect, useRef } from 'react'

/**
 * CSS class names managed by this hook (defined in global.css):
 *   .scroll-locked          – overflow: hidden
 *   .scroll-locked-fixed    – position: fixed; left/right: 0; overflow: hidden
 *   .scroll-locked-touch    – touch-action: none; overscroll-behavior: none
 *   html.scroll-locked-touch – overscroll-behavior: none
 *
 * The only inline style this hook touches is `body.style.top` because the
 * scroll-position offset is dynamic and cannot be expressed with a static CSS
 * class.  Everything else is driven by toggling classes.
 */

/**
 * Custom hook to lock/unlock body scroll in an SSR-safe way.
 *
 * Uses CSS classes instead of direct style manipulation to stay compatible
 * with React's reconciliation and avoid hydration mismatches in Next.js.
 *
 * @param {boolean} locked - Whether the body scroll should be locked.
 * @param {object} [options] - Optional configuration.
 * @param {boolean} [options.preserveScrollPosition=false] - When true, uses
 *   `position: fixed` with a negative top offset to preserve the user's scroll
 *   position while locked, and restores it on unlock. Useful for mobile
 *   overlays (e.g., chatbot, mobile nav) where the user should return to their
 *   previous scroll position after closing. When false, simply sets
 *   `overflow: hidden` on the body.
 * @param {boolean} [options.disableTouchActions=false] - When true, also sets
 *   `touch-action: none` and `overscroll-behavior: none` on the body and
 *   document element to fully prevent mobile touch scrolling and
 *   overscroll/bounce effects.
 */
const useBodyScrollLock = (locked, options = {}) => {
  const { preserveScrollPosition = false, disableTouchActions = false } = options
  const scrollYRef = useRef(0)

  useEffect(() => {
    // SSR guard — effects only run client-side but this is defensive
    if (typeof document === 'undefined') return

    if (locked) {
      if (preserveScrollPosition) {
        // Capture current scroll position before locking
        scrollYRef.current = window.scrollY

        document.body.classList.add('scroll-locked-fixed')
        // Dynamic offset – must be inline since the value is runtime-dependent
        document.body.style.top = `-${scrollYRef.current}px`

        if (disableTouchActions) {
          document.body.classList.add('scroll-locked-touch')
          document.documentElement.classList.add('scroll-locked-touch')
        }
      } else {
        document.body.classList.add('scroll-locked')
      }
    }

    return () => {
      if (locked) {
        if (preserveScrollPosition) {
          document.body.classList.remove('scroll-locked-fixed')
          document.body.style.top = ''

          if (disableTouchActions) {
            document.body.classList.remove('scroll-locked-touch')
            document.documentElement.classList.remove('scroll-locked-touch')
          }

          // Restore scroll position
          window.scrollTo(0, scrollYRef.current)
        } else {
          document.body.classList.remove('scroll-locked')
        }
      }
    }
  }, [locked, preserveScrollPosition, disableTouchActions])
}

export default useBodyScrollLock
